/**
 * Comprehensive error handling utilities for VAT calculations and preferences
 */

import { validateAmount, handleVATCalculationError, getVATPreferenceFallback } from './vat-calculations';

// Error types for categorization
export const VAT_ERROR_TYPES = {
  VALIDATION: 'validation',
  CALCULATION: 'calculation',
  PREFERENCE: 'preference',
  NETWORK: 'network',
  PERMISSION: 'permission',
  UNKNOWN: 'unknown'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

/**
 * Categorize error based on error message and context
 * @param {Error} error - The error object
 * @param {Object} context - Additional context
 * @returns {Object} Error categorization
 */
export const categorizeError = (error, context = {}) => {
  const message = error.message?.toLowerCase() || '';
  const { operation = 'unknown' } = context;
  
  let type = VAT_ERROR_TYPES.UNKNOWN;
  let severity = ERROR_SEVERITY.MEDIUM;
  let userMessage = 'An unexpected error occurred. Please try again.';
  let canRetry = true;
  let suggestedAction = 'Please try again or contact support if the problem persists.';
  
  // Validation errors
  if (message.includes('validation') || message.includes('invalid') || message.includes('required') || 
      message.includes('valid number') || message.includes('must be')) {
    type = VAT_ERROR_TYPES.VALIDATION;
    severity = ERROR_SEVERITY.LOW;
    canRetry = false;
    
    if (message.includes('amount') || message.includes('valid number')) {
      userMessage = 'Please enter a valid amount.';
      suggestedAction = 'Check that the amount is a positive number.';
    } else if (message.includes('required')) {
      userMessage = 'Required information is missing.';
      suggestedAction = 'Please fill in all required fields.';
    } else {
      userMessage = 'The entered information is not valid.';
      suggestedAction = 'Please check your input and try again.';
    }
  }
  
  // Calculation errors
  else if (message.includes('calculation') || message.includes('vat') || message.includes('rate')) {
    type = VAT_ERROR_TYPES.CALCULATION;
    severity = ERROR_SEVERITY.MEDIUM;
    userMessage = 'VAT calculation failed.';
    suggestedAction = 'Please verify the entered amounts and try again.';
  }
  
  // Preference errors
  else if (message.includes('preference') || message.includes('setting')) {
    type = VAT_ERROR_TYPES.PREFERENCE;
    severity = ERROR_SEVERITY.LOW;
    userMessage = 'Unable to load your preferences.';
    suggestedAction = 'Default settings will be used. You can update preferences later.';
  }
  
  // Network errors
  else if (message.includes('network') || message.includes('timeout') || message.includes('connection')) {
    type = VAT_ERROR_TYPES.NETWORK;
    severity = ERROR_SEVERITY.MEDIUM;
    userMessage = 'Connection problem occurred.';
    suggestedAction = 'Please check your internet connection and try again.';
  }
  
  // Permission errors
  else if (message.includes('permission') || message.includes('unauthorized') || message.includes('forbidden')) {
    type = VAT_ERROR_TYPES.PERMISSION;
    severity = ERROR_SEVERITY.HIGH;
    canRetry = false;
    userMessage = 'You do not have permission to perform this action.';
    suggestedAction = 'Please contact your administrator for access.';
  }
  
  return {
    type,
    severity,
    userMessage,
    technicalMessage: error.message,
    canRetry,
    suggestedAction,
    operation,
    timestamp: new Date().toISOString()
  };
};

/**
 * Handle VAT preference loading errors with fallback
 * @param {Error} error - The error that occurred
 * @param {string} costType - The cost type (fuel or maintenance)
 * @param {Object} context - Additional context
 * @returns {Object} Error handling result with fallback
 */
export const handlePreferenceError = (error, costType, context = {}) => {
  const categorized = categorizeError(error, { operation: 'preference_loading', ...context });
  
  console.warn(`VAT preference loading failed for ${costType}:`, error);
  
  // Provide fallback preference
  const fallbackPreference = getVATPreferenceFallback(costType, error);
  
  return {
    success: false,
    error: categorized,
    fallback: {
      value: fallbackPreference,
      source: 'fallback',
      reliable: false
    },
    userNotification: {
      type: 'warning',
      message: `Unable to load your ${costType} VAT preference. Using default setting.`,
      action: 'You can update this preference in settings later.'
    }
  };
};

/**
 * Handle form validation errors
 * @param {Array} errors - Array of validation errors
 * @param {Object} context - Additional context
 * @returns {Object} Formatted validation result
 */
export const handleValidationErrors = (errors, context = {}) => {
  const { formType = 'costing' } = context;
  
  if (!Array.isArray(errors) || errors.length === 0) {
    return {
      hasErrors: false,
      errors: [],
      canSubmit: true
    };
  }
  
  const categorizedErrors = errors.map(error => {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    return categorizeError(errorObj, { operation: 'form_validation', formType });
  });
  
  // Separate critical errors from warnings
  const criticalErrors = categorizedErrors.filter(e => 
    e.severity === ERROR_SEVERITY.HIGH || e.severity === ERROR_SEVERITY.CRITICAL
  );
  
  const warnings = categorizedErrors.filter(e => 
    e.severity === ERROR_SEVERITY.LOW || e.severity === ERROR_SEVERITY.MEDIUM
  );
  
  return {
    hasErrors: categorizedErrors.length > 0,
    hasCriticalErrors: criticalErrors.length > 0,
    errors: categorizedErrors,
    criticalErrors,
    warnings,
    canSubmit: criticalErrors.length === 0,
    summary: {
      total: categorizedErrors.length,
      critical: criticalErrors.length,
      warnings: warnings.length
    }
  };
};

/**
 * Create user-friendly error messages for different contexts
 * @param {Object} error - Categorized error object
 * @param {Object} context - Additional context
 * @returns {Object} User-friendly error information
 */
export const createUserErrorMessage = (error, context = {}) => {
  const { showTechnicalDetails = false, includeActions = true } = context;
  
  const message = {
    title: 'Error',
    description: error.userMessage,
    type: 'error'
  };
  
  // Customize based on error type
  switch (error.type) {
    case VAT_ERROR_TYPES.VALIDATION:
      message.title = 'Input Error';
      message.type = 'warning';
      break;
      
    case VAT_ERROR_TYPES.CALCULATION:
      message.title = 'Calculation Error';
      break;
      
    case VAT_ERROR_TYPES.PREFERENCE:
      message.title = 'Settings Error';
      message.type = 'info';
      break;
      
    case VAT_ERROR_TYPES.NETWORK:
      message.title = 'Connection Error';
      break;
      
    case VAT_ERROR_TYPES.PERMISSION:
      message.title = 'Access Denied';
      message.type = 'error';
      break;
  }
  
  // Add suggested actions
  if (includeActions && error.suggestedAction) {
    message.action = error.suggestedAction;
  }
  
  // Add technical details if requested
  if (showTechnicalDetails && error.technicalMessage) {
    message.technicalDetails = error.technicalMessage;
  }
  
  // Add retry option
  if (error.canRetry) {
    message.canRetry = true;
    message.retryText = 'Try Again';
  } else {
    message.canRetry = false;
  }
  
  return message;
};

/**
 * Handle server-side validation errors
 * @param {Object} serverResponse - Server response with validation errors
 * @returns {Object} Processed validation errors
 */
export const handleServerValidationErrors = (serverResponse) => {
  const errors = [];
  
  if (serverResponse.errors && Array.isArray(serverResponse.errors)) {
    errors.push(...serverResponse.errors);
  } else if (serverResponse.error) {
    errors.push(serverResponse.error);
  } else if (serverResponse.message) {
    errors.push(serverResponse.message);
  }
  
  return handleValidationErrors(errors, { formType: 'server_validation' });
};

/**
 * Create error recovery strategies
 * @param {Object} error - Categorized error object
 * @param {Object} context - Additional context
 * @returns {Object} Recovery strategies
 */
export const createRecoveryStrategies = (error, context = {}) => {
  const strategies = [];
  
  switch (error.type) {
    case VAT_ERROR_TYPES.VALIDATION:
      strategies.push({
        type: 'user_action',
        description: 'Correct the input and try again',
        priority: 'high'
      });
      break;
      
    case VAT_ERROR_TYPES.CALCULATION:
      strategies.push({
        type: 'retry',
        description: 'Retry the calculation',
        priority: 'medium'
      });
      strategies.push({
        type: 'fallback',
        description: 'Use manual calculation',
        priority: 'low'
      });
      break;
      
    case VAT_ERROR_TYPES.PREFERENCE:
      strategies.push({
        type: 'fallback',
        description: 'Use default preferences',
        priority: 'high'
      });
      strategies.push({
        type: 'retry',
        description: 'Retry loading preferences',
        priority: 'medium'
      });
      break;
      
    case VAT_ERROR_TYPES.NETWORK:
      strategies.push({
        type: 'retry',
        description: 'Retry the request',
        priority: 'high'
      });
      strategies.push({
        type: 'offline_mode',
        description: 'Continue with cached data',
        priority: 'medium'
      });
      break;
      
    case VAT_ERROR_TYPES.PERMISSION:
      strategies.push({
        type: 'contact_admin',
        description: 'Contact administrator for access',
        priority: 'high'
      });
      break;
  }
  
  return strategies;
};

/**
 * Log error for monitoring and debugging
 * @param {Object} error - Categorized error object
 * @param {Object} context - Additional context
 */
export const logError = (error, context = {}) => {
  const logData = {
    timestamp: new Date().toISOString(),
    error: {
      type: error.type,
      severity: error.severity,
      message: error.technicalMessage,
      userMessage: error.userMessage
    },
    context: {
      operation: error.operation,
      ...context
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
    url: typeof window !== 'undefined' ? window.location.href : 'server'
  };
  
  // Log based on severity
  switch (error.severity) {
    case ERROR_SEVERITY.CRITICAL:
      console.error('CRITICAL VAT Error:', logData);
      break;
    case ERROR_SEVERITY.HIGH:
      console.error('HIGH VAT Error:', logData);
      break;
    case ERROR_SEVERITY.MEDIUM:
      console.warn('MEDIUM VAT Error:', logData);
      break;
    case ERROR_SEVERITY.LOW:
      console.info('LOW VAT Error:', logData);
      break;
    default:
      console.log('VAT Error:', logData);
  }
  
  // In production, you might want to send this to an error tracking service
  // Example: errorTrackingService.captureError(logData);
};

// Export all utilities
export default {
  VAT_ERROR_TYPES,
  ERROR_SEVERITY,
  categorizeError,
  handlePreferenceError,
  handleValidationErrors,
  createUserErrorMessage,
  handleServerValidationErrors,
  createRecoveryStrategies,
  logError
};